import React, { useCallback, useMemo, useState } from "react";

import { Dialog } from "@material-ui/core";
import {
  Formik,
  FormikErrors,
  FormikHelpers,
  getIn,
  useFormikContext,
} from "formik";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { ConfirmDialog } from "components/ConfirmDialog";
import { patchSaleLot, addSaleLotWithPens } from "actions/saleLots";
import { SaleBuyerSplitAction } from "actions/buyerSplits";
import { SaleVendorSplitAction } from "actions/vendorSplits";
import {
  getActiveLivestockAgentDeployment,
  getConsignmentById,
  getConsignments,
  getCurrentSale,
  getPreviousSaleLotByConsignmentId,
  getSaleLotById,
  selectCurrentMixedBreedId,
  selectPreviousSaleLotByConsignmentIdLookup,
} from "selectors";
import { calculateTotalPriceCents, getUnitPriceString } from "lib";

import { SaleType } from "constants/sale";

import { Form } from "../Form/Form";
import { Actions } from "../Form/Actions";
import { ModalTitle } from "../Form/ModalTitle";
import { editSaleLotValidationSchema } from "../Form/validation";
import {
  TransactionalSaleFormValues,
  SaleLotFormValues,
  defaultSaleLot,
} from "../lib/types";
import {
  buyerSplitNamespace,
  vendorSplitNamespace,
  TransactionalSaleFormTabId,
  TransactionalSaleFormTabs,
  saleLotNamespace,
} from "../constants";
import { resolveCopiedOrPreviousSaleLotValues } from "lib/saleLot/resolveCopiedOrPreviousSaleLotValues";
import { validateAdvancedDrafting } from "lib/saleLot/validateAdvancedDrafting";
import { PricingTypes } from "constants/pricingTypes";
import { useAdvancedDraftingMirrorEffects } from "hooks";
import { getMobileTransactionalSaleLotModalTabOrder } from "../constants/saleLot";
import { PriceTab } from "../Tabs/PriceTab";
import { NlisTab } from "../Tabs/NlisTab";
import { ReviewTab } from "../Tabs/ReviewTab";
import { WhoTab } from "./tabs/WhoTab";
import { WhatTab } from "./tabs/WhatTab";

interface ModalBodyProps {
  saleLotId?: string;
  speciesId: number | null;
  saleType: SaleType | null;
  saleSubTypeId: number | null;
  deploymentId: number | undefined;
  onClose: () => void;
  tab?: TransactionalSaleFormTabId;
  changeTab: (tab: TransactionalSaleFormTabId) => void;
  tabOrder: TransactionalSaleFormTabId[];
  title: string;
}

const MobileTransactionalEditSaleLotModalBody = ({
  saleLotId,
  speciesId,
  saleType,
  saleSubTypeId,
  deploymentId,
  onClose,
  tab,
  changeTab,
  tabOrder,
  title,
}: ModalBodyProps) => {
  const { setFieldValue, touched } =
    useFormikContext<TransactionalSaleFormValues>();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const handleClose = () => {
    // If the user has touched any fields or is a new lot - open confirm modal
    if (Object.keys(touched).length > 0 || !saleLotId) {
      setIsConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const consignmentByIdLookup = useSelector(getConsignments);
  const previousSaleLotByConsignmentIdLookup = useSelector(
    selectPreviousSaleLotByConsignmentIdLookup,
  );

  const handleConsignmentChange = (newConsignmentId: string) => {
    if (saleLotId || !newConsignmentId) return;
    const newConsignment = consignmentByIdLookup[newConsignmentId];
    if (!newConsignment) return;
    const newPreviousSaleLot =
      previousSaleLotByConsignmentIdLookup[newConsignmentId];
    const result = resolveCopiedOrPreviousSaleLotValues({
      isNew: true,
      previousSaleLot: newPreviousSaleLot,
      consignment: newConsignment,
      speciesId,
    });
    Object.entries(result).forEach(([key, value]) => {
      if (getIn(touched, `saleLot.${key}`)) return;
      setFieldValue(`saleLot.${key}`, value, false);
    });
  };

  useAdvancedDraftingMirrorEffects(saleLotNamespace);

  const activeTab = tab || TransactionalSaleFormTabs.WHO;

  const renderTab = (
    currentTab: TransactionalSaleFormTabId,
  ): React.ReactNode => {
    switch (currentTab) {
      case TransactionalSaleFormTabs.WHO:
        return (
          <WhoTab
            saleLotNamespace={saleLotNamespace}
            vendorSplitNamespace={vendorSplitNamespace}
            buyerSplitNamespace={buyerSplitNamespace}
            saleLotId={saleLotId}
            onConsignmentChange={handleConsignmentChange}
          />
        );
      case TransactionalSaleFormTabs.WHAT:
        return (
          <WhatTab
            saleLotNamespace={saleLotNamespace}
            deploymentId={deploymentId}
            speciesId={speciesId}
            saleType={saleType}
            saleSubTypeId={saleSubTypeId}
            saleLotId={saleLotId}
            onClose={onClose}
          />
        );
      case TransactionalSaleFormTabs.PRICE:
        return (
          <PriceTab saleLotNamespace={saleLotNamespace} saleType={saleType} />
        );
      case TransactionalSaleFormTabs.NLIS:
        return saleLotId ? (
          <NlisTab saleLotId={saleLotId} saleLotNamespace={saleLotNamespace} />
        ) : null;
      case TransactionalSaleFormTabs.REVIEW:
        return <ReviewTab deploymentId={deploymentId} speciesId={speciesId} />;
      default:
        return null;
    }
  };

  return (
    <>
      <ModalTitle onClose={handleClose}>{title}</ModalTitle>
      <Form
        activeTab={activeTab}
        changeTab={changeTab}
        tabOrder={tabOrder}
        saleLotNamespace={saleLotNamespace}
        renderTab={renderTab}
      />
      <Actions
        onClose={handleClose}
        activeTab={activeTab}
        changeTab={changeTab}
        tabOrder={tabOrder}
      />
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Are you sure you want to exit?"
        message="You will lose your unsaved changes."
        buttonMessage="Confirm"
        onCancel={() => setIsConfirmOpen(false)}
        onDelete={onClose}
      />
    </>
  );
};

interface MobileTransactionalEditSaleLotModalProps {
  saleLotId?: string;
  consignmentId?: string;
  onClose: () => void;
  tab?: TransactionalSaleFormTabId;
  changeTab: (tab: TransactionalSaleFormTabId) => void;
}

export const MobileTransactionalEditSaleLotModal = ({
  saleLotId,
  consignmentId,
  onClose,
  tab,
  changeTab,
}: MobileTransactionalEditSaleLotModalProps) => {
  const dispatch = useDispatch();

  const storeSaleLot = useSelector(getSaleLotById(saleLotId));
  const currentSale = useSelector(getCurrentSale);
  const speciesId = currentSale?.species_id ?? null;
  const saleType = currentSale?.sale_type ?? null;
  const saleSubTypeId = currentSale?.sale_sub_type_id ?? null;

  const consignmentsLookup = useSelector(getConsignments);

  const defaultVendorId = currentSale?.default_vendor_id ?? null;
  const defaultVendorPropertyId =
    currentSale?.default_vendor_property_id ?? null;

  const defaultConsignmentId = useMemo<string | null>(() => {
    if (!defaultVendorId || !defaultVendorPropertyId) return null;
    const consignments = Object.values(consignmentsLookup) as Consignment[];
    const matches = consignments.filter(
      c =>
        c.vendor_id === defaultVendorId &&
        c.vendor_property_id === defaultVendorPropertyId,
    );
    // Only auto-fill when unambiguous - multiple matches means we can't safely pick one.
    return matches.length === 1 ? matches[0].id : null;
  }, [consignmentsLookup, defaultVendorId, defaultVendorPropertyId]);

  const effectiveConsignmentId =
    storeSaleLot?.consignment_id ??
    consignmentId ??
    defaultConsignmentId ??
    null;
  const consignment = useSelector(getConsignmentById(effectiveConsignmentId));
  const previousSaleLot: SaleLot | null = useSelector(
    getPreviousSaleLotByConsignmentId(effectiveConsignmentId),
  );

  const activeDeployment = useSelector(getActiveLivestockAgentDeployment);
  const deploymentId = activeDeployment?.id;
  const livestockSaleId = currentSale?.id ?? null;
  const mixedBreedId = useSelector(selectCurrentMixedBreedId);

  const handleSubmit = useCallback(
    (
      values: TransactionalSaleFormValues,
      { setErrors, setSubmitting }: FormikHelpers<TransactionalSaleFormValues>,
    ) => {
      const { saleLot, vendorSplit, buyerSplit } = values;

      const draftingErrors = validateAdvancedDrafting(saleLot, mixedBreedId);
      if (draftingErrors) {
        setErrors({
          saleLot: draftingErrors as unknown as FormikErrors<SaleLotFormValues>,
        });
        setSubmitting(false);
        changeTab(TransactionalSaleFormTabs.WHAT);
        return;
      }

      const totalPriceCents = calculateTotalPriceCents({
        unitPrice: saleLot.unit_price ? parseFloat(saleLot.unit_price) : 0,
        quantity: saleLot.quantity || 0,
        pricing_type_id: saleLot.pricing_type_id,
        total_mass_grams: saleLot.total_mass_grams || 0,
      });

      const patch = {
        ...saleLot,
        total_price_cents: totalPriceCents,
      } as unknown as Partial<SaleLot>;

      if (saleLotId) {
        dispatch(
          patchSaleLot(
            { id: saleLotId, ...patch },
            { changeReason: "Updated from mobile edit sale lot" },
          ),
        );
      } else {
        const newSaleLotId = uuidv4();
        dispatch(addSaleLotWithPens(newSaleLotId, patch));

        if (vendorSplit && deploymentId) {
          const vendorIdForSplit =
            consignmentsLookup[saleLot.consignment_id ?? ""]?.vendor_id ?? null;
          if (vendorIdForSplit) {
            dispatch(
              SaleVendorSplitAction.create({
                saleLotId: newSaleLotId,
                deploymentId,
                primaryBusinessId: vendorIdForSplit,
                type: vendorSplit.type,
                splits: vendorSplit.splits,
              }),
            );
          }
        }

        if (buyerSplit && deploymentId && livestockSaleId && saleLot.buyer_id) {
          dispatch(
            SaleBuyerSplitAction.create({
              saleLotId: newSaleLotId,
              livestockSaleId,
              deploymentId,
              primaryBusinessId: saleLot.buyer_id,
              type: buyerSplit.type,
              splits: buyerSplit.splits,
            }),
          );
        }
      }
      onClose();
    },
    [
      dispatch,
      onClose,
      changeTab,
      saleLotId,
      consignmentsLookup,
      deploymentId,
      livestockSaleId,
      mixedBreedId,
    ],
  );

  const initialSaleLot: SaleLotFormValues = storeSaleLot
    ? {
        buyer_id: storeSaleLot.buyer_id ?? null,
        destination_property_id: storeSaleLot.destination_property_id ?? null,
        buyer_way: storeSaleLot.buyer_way ?? null,
        consignment_id: storeSaleLot.consignment_id ?? null,
        quantity: storeSaleLot.quantity ?? null,
        quantity_progeny: storeSaleLot.quantityProgeny ?? 0,
        product_id: storeSaleLot.product_id ?? null,
        sex_id: storeSaleLot.sex_id ?? null,
        age_id: storeSaleLot.age_id ?? null,
        breed_id: storeSaleLot.breed_id ?? null,
        dentitionId: storeSaleLot.dentitionId ?? null,
        marks: storeSaleLot.marks ?? [],
        labels: storeSaleLot.labels ?? [],
        draftingAttributes: storeSaleLot.draftingAttributes ?? null,
        exemption_id: storeSaleLot.exemption_id ?? null,
        estimatedAverageMassGrams: storeSaleLot.estimatedAverageMassGrams ?? 0,
        estimatedAverageWeightId: storeSaleLot.estimatedAverageWeightId ?? null,
        dressingRangeId: storeSaleLot.dressingRangeId ?? null,
        grade_id: storeSaleLot.grade_id ?? null,
        category_id: storeSaleLot.category_id ?? null,
        note: storeSaleLot.note ?? "",
        pricing_type_id: storeSaleLot.pricing_type_id ?? PricingTypes.PER_KILO,
        unit_price: getUnitPriceString({
          total_price_cents: storeSaleLot.total_price_cents,
          total_mass_grams: storeSaleLot.total_mass_grams,
          pricing_type_id: storeSaleLot.pricing_type_id,
          quantity: storeSaleLot.quantity,
        }),
        total_mass_grams: storeSaleLot.total_mass_grams ?? 0,
        quantity_delivered: storeSaleLot.quantity_delivered ?? null,
        reservePrice: storeSaleLot.reservePrice ?? null,
        invoiceToBusinessId: storeSaleLot.invoiceToBusinessId ?? null,
        quantityTagsUsed: storeSaleLot.quantityTagsUsed ?? 0,
        youtube_link: storeSaleLot.youtube_link ?? "",
        sale_round_id: storeSaleLot.sale_round_id ?? null,
      }
    : {
        ...defaultSaleLot,
        buyer_id: currentSale?.default_buyer_id ?? null,
        destination_property_id: currentSale?.default_property_id ?? null,
        consignment_id: consignmentId ?? defaultConsignmentId ?? null,
        sale_round_id: currentSale?.rounds?.[0] ?? null,
      };

  const draftingAndCarryOver = resolveCopiedOrPreviousSaleLotValues({
    isNew: !saleLotId,
    saleLot: storeSaleLot,
    previousSaleLot,
    consignment,
    speciesId,
  });

  const initialValues: TransactionalSaleFormValues = {
    saleLot: {
      ...initialSaleLot,
      ...draftingAndCarryOver,
    } as SaleLotFormValues,
    ...(saleLotId ? {} : { vendorSplit: null, buyerSplit: null }),
  };

  const title = saleLotId ? "Edit Sale Lot" : "New Sale Lot";
  const tabOrder = getMobileTransactionalSaleLotModalTabOrder(saleLotId);

  return (
    <Dialog fullWidth fullScreen onClose={onClose} open>
      <Formik
        initialValues={initialValues}
        validationSchema={editSaleLotValidationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={false}
      >
        <MobileTransactionalEditSaleLotModalBody
          saleLotId={saleLotId}
          speciesId={speciesId}
          saleType={saleType}
          saleSubTypeId={saleSubTypeId}
          deploymentId={deploymentId}
          onClose={onClose}
          tab={tab}
          changeTab={changeTab}
          tabOrder={tabOrder}
          title={title}
        />
      </Formik>
    </Dialog>
  );
};
